import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Optional,
  Output,
  ViewEncapsulation,
} from '@angular/core'
import { FormGroup, FormControl, Validators, ValidatorFn, AbstractControl } from '@angular/forms'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import {
  BaseEventTicketEntity,
  EventTicketGuestStatusEnum,
  EventTicketGuestStepperPayload,
} from 'libs/shared/event-tickets/data-access/src/lib/+state/base-event-tickets.models'
import { SharedTableNumbersFacade } from 'libs/shared/models/table-numbers/table-numbers.models'
import { FormUtils } from 'libs/shared/utils/helpers/src/lib/form-utils'
import { GbCustomValidators } from 'libs/shared/utils/helpers/src/lib/form-validators/custom-form.validator'
import { GbFormService } from 'libs/shared/utils/services/src/lib/gb-form-service/gb-form.service'
import { isEqual } from 'lodash'
import { combineLatest, distinctUntilChanged, Observable, of, startWith, take, tap } from 'rxjs'
import { CaptureAuctionFieldEnum, SharedAuctionFacade } from 'libs/shared/models/auction/auction.models'
import { VerifiedStateEnum } from 'libs/shared/models/shared.models'
import { SharedProfile, SharedProfileFacade } from 'libs/shared/models/profile/profile.models'
import { GbCookieService } from 'libs/shared/utils/services/src/lib/gb-cookie/gb-cookie.service'

@UntilDestroy()
@Component({
  selector: 'gb-event-ticket-guest-form',
  templateUrl: './gb-event-ticket-guest-form.component.html',
  styleUrls: ['./gb-event-ticket-guest-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class GbEventTicketGuestFormComponent implements OnInit {
  @Output() onSubmitEvent = new EventEmitter<EventTicketGuestStepperPayload>()
  @Output() onSelfAssignedChange = new EventEmitter<boolean>()
  @Output() formReady = new EventEmitter<FormGroup>()

  @Input() eventTicket!: BaseEventTicketEntity
  // if form should prepopulate profile (i.e. not on 'transfer' and subsequent multi-assign forms)
  @Input() shouldPrepopulateForm = true
  // if checkbox should be shown (i.e. not shown on 'edit', 'accept', or 'transfer' when self-assigned)
  @Input() shouldShowSelfAssign = true
  // if multiple guest forms are shown (i.e. part of ticket purchase flow and is second form onwards)
  @Input() isMultiAssign = false
  // if email can be edited (on 'accept' if user not logged in)
  @Input() canEditEmail? = false
  // if form is shown as part of volunteer check-in
  @Input() isCheckIn?: boolean
  // submit label shown in parent form (used in invite email text)
  @Input() buttonLabel = ''

  guestForm!: FormGroup
  capturePhoneNumbers?: CaptureAuctionFieldEnum
  isSelfAssigned = false
  isRequired = true

  selectedProfile$!: Observable<SharedProfile | null>
  auctionCountry$ = this.auctionFacade.auctionCountry$
  capturePhoneNumbers$ = this.auctionFacade.capturePhoneNumbers$
  allTableNumbers$ = this.tableNumbersFacade.allTableNumbers$

  /**
   * Purchasers should not be able to update profile fields on
   * self-assigning (i.e. on 'assign' or 'transfer') or for
   * an accepted ticket assigned to someone else.
   */
  get shouldDisableForm(): boolean {
    const isSelfAssigning = this.isSelfAssigned && this.shouldShowSelfAssign
    const isTicketAcceptedByOther =
      !this.isSelfAssigned && this.eventTicket.guest_status !== EventTicketGuestStatusEnum.InviteSent

    return isSelfAssigning || isTicketAcceptedByOther
  }

  get shouldShowTableField(): boolean {
    return !!(this.shouldPrepopulateForm && this.eventTicket.profile)
  }

  get shouldShowPhoneField(): boolean {
    return this.capturePhoneNumbers !== CaptureAuctionFieldEnum.Off
  }

  get emailFormControl(): AbstractControl<string> | null {
    return this.guestForm.get('email')
  }

  get shouldShowInviteEmailText(): boolean {
    return !!(!this.isSelfAssigned && this.emailFormControl?.enabled && this.emailFormControl?.value)
  }

  get phoneFormControl(): AbstractControl<string> | null {
    return this.guestForm.get('phone_number')
  }

  get isEmailVerified(): boolean {
    return this.eventTicket.profile?.email_verified_state === VerifiedStateEnum.Verified
  }

  /**
   * Verification only sent on check-in where email is unverified
   */
  get shouldShowVerifyEmailText(): boolean {
    return !!(this.isAdminOrVolunteer && this.isCheckIn && !this.isEmailVerified)
  }

  get isAdminOrVolunteer(): boolean {
    return this.gbCookieService.isAdminOrVolunteer()
  }

  constructor(
    @Inject('AuctionFacade') private auctionFacade: SharedAuctionFacade,
    @Inject('TableNumbersFacade') private tableNumbersFacade: SharedTableNumbersFacade,
    @Optional() @Inject('SelectedProfileFacade') private selectedProfileFacade: SharedProfileFacade,
    private gbCookieService: GbCookieService,
    private gbFormService: GbFormService,
  ) {
    this.selectedProfile$ = this.selectedProfileFacade ? this.selectedProfileFacade.selectedProfile$ : of(null)
  }

  ngOnInit(): void {
    this.setupForm()
  }

  private setupForm(): void {
    this.createFormControls()
    this.initPhoneFormControl()
    this.shouldPrepopulateForm && this.prepopulateForm()
    this.isMultiAssign ? this.setValidatorsOnFormChanges() : this.setValidators()
    this.formReady.emit(this.guestForm)
  }

  private createFormControls(): void {
    this.guestForm = new FormGroup({
      first_name: new FormControl('', { nonNullable: true }),
      last_name: new FormControl('', { nonNullable: true }),
      email: new FormControl(''),
    })

    if (this.shouldShowTableField) {
      this.guestForm.addControl('table_number', new FormControl(''))
    }
  }

  private initPhoneFormControl(): void {
    combineLatest([this.capturePhoneNumbers$, this.selectedProfile$])
      .pipe(
        distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
        tap(([capturePhoneNumbers]) => (this.capturePhoneNumbers = capturePhoneNumbers as CaptureAuctionFieldEnum)),
        untilDestroyed(this),
      )
      .subscribe(([, selectedProfile]) => {
        this.guestForm.removeControl('phone_number')
        if (this.shouldShowPhoneField) {
          const phoneControl = this.createPhoneControl(selectedProfile)
          this.guestForm.addControl('phone_number', phoneControl)
          this.setValidators({ phone_number: this.phoneFormControl as FormControl })
        }
      })
  }

  private createPhoneControl(selectedProfile?: SharedProfile | null): FormControl {
    let value = ''
    if (this.shouldPrepopulateForm) {
      const profile = this.isSelfAssigned && selectedProfile ? selectedProfile : this.eventTicket.profile
      value = profile ? this.getProfilePhone(profile) : ''
    }
    return new FormControl(value, { nonNullable: true })
  }

  private prepopulateForm(): void {
    const handleFormPopulation = (profile?: SharedProfile | null): void => {
      this.setIsSelfAssigned(profile)
      this.patchValues(this.eventTicket.profile || profile)
      this.shouldDisableForm ? this.disableForm() : this.disableUneditableFields()
    }

    if (this.isAdminOrVolunteer) {
      handleFormPopulation(this.eventTicket.purchaser)
    } else {
      this.selectedProfile$.pipe(take(1)).subscribe((selectedProfile) => handleFormPopulation(selectedProfile))
    }
  }
  /**
   * Sets isSelfAssigned to true under any of the following conditions:
   * - The user's profile matches the profile associated with the ticket.
   * - The ticket is not assigned to any profile.
   * - The user is not logged in, indicating an acceptance of the ticket.
   */
  private setIsSelfAssigned(profile?: SharedProfile | null): void {
    const emailMatches = profile && [profile.email, profile.invite_email].includes(this.eventTicket.profile?.email)
    this.isSelfAssigned = emailMatches || !this.eventTicket.profile?.uuid || !profile
    this.onSelfAssignedChange.emit(this.isSelfAssigned)
  }

  private disableForm(): void {
    Object.keys(this.guestForm.controls).forEach((key) => {
      const control = this.guestForm.get(key)
      if (control?.value) control.disable()
    })
  }

  private disableUneditableFields(): void {
    const fieldsToDisable: string[] = []
    if (this.emailFormControl?.value && !this.canEditEmail) fieldsToDisable.push('email')
    if (this.shouldShowTableField && !this.isAdminOrVolunteer) fieldsToDisable.push('table_number')

    fieldsToDisable.forEach((field) => this.guestForm.controls[field].disable())
  }

  /**
   * Set validators on form changes for multi-assign forms.
   * Form only required if one or more fields have been filled.
   */
  private setValidatorsOnFormChanges(): void {
    this.guestForm.valueChanges.pipe(startWith({}), untilDestroyed(this)).subscribe(() => {
      this.isRequired = !FormUtils.isFormEmpty(this.guestForm)
      this.setValidators()
    })
  }

  private setValidators(controls = this.guestForm.controls): void {
    for (const key in controls) {
      const control = controls[key]
      control.setValidators(this.getValidatorsForKey(key))
      control.updateValueAndValidity({ emitEvent: false })
    }
  }

  private getValidatorsForKey(key: string): ValidatorFn[] | null {
    switch (key) {
      case 'email':
        return this.getEmailValidators()
      case 'phone_number':
        return this.getPhoneValidators()
      case 'table_number':
        return null
      default:
        return this.isRequired ? [Validators.required] : null
    }
  }

  private getEmailValidators(): ValidatorFn[] | null {
    const validators = [Validators.email, GbCustomValidators.emailMatchValidator(this.eventTicket.purchaser.email)]
    if (this.isRequired) validators.push(Validators.required)
    return validators
  }

  /**
   * Phone number required if ticket assigned to current user
   * and campaign requires capturing phone number.
   */
  private getPhoneValidators(): ValidatorFn[] | null {
    return this.isSelfAssigned && this.capturePhoneNumbers === CaptureAuctionFieldEnum.Required
      ? [Validators.required]
      : null
  }

  toggleIsSelfAssigned(profile: SharedProfile): void {
    this.isSelfAssigned = !this.isSelfAssigned
    this.onSelfAssignedChange.emit(this.isSelfAssigned)
    this.isSelfAssigned ? this.patchAndDisableForm(profile) : this.resetForm()
    setTimeout(() => this.setValidators())
  }

  private patchAndDisableForm(profile: SharedProfile): void {
    this.patchValues(profile)
    this.disableForm()
  }

  private patchValues(profile?: SharedProfile | null): void {
    if (!profile) return
    const values = {
      first_name: profile.first_name,
      last_name: profile.last_name,
      email: this.getProfileEmail(profile),
      phone_number: 'phone_number' in profile ? this.getProfilePhone(profile) : undefined,
      // do not patch 'profile' table number to event ticket
      table_number: 'table_number' in profile ? undefined : String(this.eventTicket.table_number),
    }

    this.gbFormService.patchValues(values, this.guestForm)
  }

  private getProfileEmail(profile: SharedProfile): string {
    return profile.email || ('invite_email' in profile ? profile.invite_email || '' : '')
  }

  private getProfilePhone(profile?: SharedProfile): string {
    if (!profile) return ''
    return profile.phone_number || ('invite_phone_number' in profile ? profile.invite_phone_number || '' : '')
  }

  private resetForm(): void {
    this.guestForm.reset()
    this.guestForm.get('phone_number')?.setValue('')
    this.guestForm.enable()
  }
}
